<template>
    <div>
        <!--        <loading :loading="paymentMethodsLoading"/>-->
        <template v-if="!paymentMethodsLoading">
            <template v-if="user && parseFloat(user.credit_balance) > 0 && paymentMethods.find((paymentMethod)=> paymentMethod.type === 'invoice') && allowCredits">
                <div class="content-heading">
                    Choose amount of credits to use
                </div>
                <div>
                    <div id="credits-to-use-group" class="form-group">
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <span class="input-group-text">$</span>
                            </div>
                            <input v-model="creditsToBeUsed" type="text" class="form-control" autocomplete="off" @keydown="numberOnlyInput">
                            <div class="input-group-append">
                                <span class="input-group-text"> / ${{ maxCreditsToBeUsed }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="divider-2"/>
            </template>
            <template v-if="paymentMethods.length > 1">
                <div class="content-heading" :class="{'disabled': total === 0}">
                    Select Payment Method
                </div>
                <div id="payment-method-options" :class="{'disabled': total === 0}">
                    <b-form-group>
                        <b-form-radio v-for="paymentMethod in paymentMethods" :key="paymentMethod.type" v-model="selectedPaymentMethod" name="payment-method-selection" :value="paymentMethod" :disabled="total === 0">
                            <b-icon-credit-card v-if="paymentMethod.type === 'credit_card'"/>
                            <b-icon-file-ruled v-else/>
                            {{ paymentMethod.name }}
                        </b-form-radio>
                    </b-form-group>
                </div>
            </template>
            <template v-if="selectedPaymentMethod && selectedPaymentMethod.type === 'credit_card' && total > 0">
                <div class="content-heading">
                    Credit Card Details
                </div>
                <div v-if="selectedPaymentMethod.lastFourDigits">
                    <div>
                        <div class="existing-cc-row">
                            <div>&lowast;&lowast;&lowast;&lowast;-&lowast;&lowast;&lowast;&lowast;-&lowast;&lowast;&lowast;&lowast;-{{ selectedPaymentMethod.lastFourDigits }}</div>
                            <div>{{ selectedPaymentMethod.expiryMonth }}/{{ selectedPaymentMethod.expiryYear }}</div>
                        </div>
                        <button v-if="user.permissions.includes('MANAGE_BILLING')" class="btn btn-default btn-hollow" @click="removeCreditCard">Remove</button>
                    </div>
                </div>
                <div v-else>
                    <div v-if="!user.permissions.includes('MANAGE_BILLING')" class="alert alert-warning mt-4">
                        You don't have permissions to set billing details
                    </div>
                    <div v-else>
                        <CreditCard v-if="stripeJsLoaded" ref="creditCard" @ready="creditCardReady"/>
                        <b-form-checkbox id="save-card" v-model="saveCard" name="save-card">
                            Save card for future use
                        </b-form-checkbox>
                    </div>
                </div>
            </template>
            <div v-if="paymentMethods.length > 1 || (selectedPaymentMethod && selectedPaymentMethod.type === 'credit_card' && total > 0)" class="divider-2"/>
        </template>
    </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import CreditCard from '~/components/checkout/CreditCard.vue'
import Loading from '~/components/global/Loading.vue'

export default {
    name: 'PaymentMethods',
    components: {
        Loading,
        CreditCard
    },
    props: {
        stripeJsLoaded: {
            type: Boolean,
            default: false
        },
        total: {
            type: Number,
            default: 0
        },
        subTotal: {
            type: Number,
            default: 0
        },
        allowCredits: {
            type: Boolean,
            default: true
        }
    },
    data () {
        return {
            paymentMethods: null,
            paymentMethodsLoading: true,
            saveCard: false,
            selectedPaymentMethod: null,
            creditsToBeUsed: 0,
            maxCreditsToBeUsed: 0
        }
    },
    computed: {
        ...mapGetters({
            user: 'auth/user'
        })
    },
    watch: {
        selectedPaymentMethod (newSelectedPaymentMethod, oldSelectedPaymentMethod) {
            if (newSelectedPaymentMethod) {
                if (newSelectedPaymentMethod.type === 'invoice') {
                    this.$emit('ready', true)
                } else if (newSelectedPaymentMethod.type === 'credit_card') {
                    this.creditCardReady()
                }
            }
        },
        creditsToBeUsed (newCreditsToBeUsed, oldCreditsToBeUsed) {
            if (newCreditsToBeUsed < 0) {
                this.creditsToBeUsed = oldCreditsToBeUsed
                return
            }

            if (newCreditsToBeUsed > this.maxCreditsToBeUsed) {
                this.creditsToBeUsed = this.maxCreditsToBeUsed
                return
            }

            this.$emit('credits-to-be-used-changed', newCreditsToBeUsed)

            this.$nextTick(function () {
                if (this.total === 0) {
                    this.selectedPaymentMethod = null
                    this.$emit('ready', true)
                }
            })
        },
        'user.credit_balance' (newUser, oldUser) {
            if (!this.checkingOut && this.user) {
                this.checkAccountCredits()
            }
        }
    },
    mounted () {
        if (this.user) {
            this.getUsersPaymentMethods()

            this.checkAccountCredits()
        }
    },
    methods: {
        checkAccountCredits () {
            if (this.allowCredits) {
                const creditBalance = parseFloat(this.user.credit_balance)
                this.creditsToBeUsed = this.maxCreditsToBeUsed = Math.min(creditBalance, this.subTotal)
            } else {
                this.creditsToBeUsed = 0
            }

            this.$emit('credits-to-be-used-changed', this.creditsToBeUsed)
        },
        creditCardReady () {
            this.$nextTick(function () {
                if ((!this.$refs.creditCard || !this.user.permissions.includes('MANAGE_BILLING')) &&
                    !(this.selectedPaymentMethod.lastFourDigits && this.selectedPaymentMethod.expiryMonth && this.selectedPaymentMethod.expiryYear)) {
                    this.$emit('ready', false)
                } else {
                    this.$emit('ready', true)
                }
            })
        },
        getUsersPaymentMethods () {
            this.paymentMethodsLoading = true
            axios.get('/account/payment-method/all')
                .then(rsp => rsp.data)
                .then((rsp) => {
                    this.paymentMethods = rsp

                    if (this.paymentMethods.length === 1) {
                        this.selectedPaymentMethod = this.paymentMethods[0]
                    }
                })
                .finally(() => {
                    this.paymentMethodsLoading = false
                    this.$emit('loaded', true)
                })
        },
        isPaymentMethodReady () {
            if (this.total === 0) {
                return true
            }

            if (!this.selectedPaymentMethod) {
                return false
            }

            if (this.selectedPaymentMethod.type === 'invoice') {
                return true
            } else if (this.selectedPaymentMethod.type === 'credit_card') {
                return !this.$refs.creditCard
            }
        },
        getPaymentMethodType () {
            return this.selectedPaymentMethod?.type
        },
        async getCreditCardToken () {
            if (this.selectedPaymentMethod.type === 'credit_card' && !this.selectedPaymentMethod.lastFourDigits) {
                return {
                    tokenData: await this.$refs.creditCard.getCreditCardToken(),
                    saveCard: this.saveCard
                }
            }

            return null
        },
        removeCreditCard () {
            this.selectedPaymentMethod.lastFourDigits = null
            this.selectedPaymentMethod.expiryMonth = null
            this.selectedPaymentMethod.expiryYear = null

            this.$emit('ready', true)

            axios.delete('/account/payment-method')
        }
    }
}
</script>
